import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useMe } from "../api/auth";
import { ApiError } from "../api/client";

/**
 * Wraps the authenticated app. While `useMe` is loading, shows a
 * spinner. On 401, redirects to /login (with `?next=` so we return
 * the user to the page they wanted after sign-in). On 403, sends to
 * /signup so the very first user can bootstrap.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();

  if (me.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted dark:text-muted-dark">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      const next = encodeURIComponent(location.pathname + location.search);
      // First-run bootstrap: server returns 401 for everyone; let
      // /login link through to /signup.
      return <Navigate to={`/login?next=${next}`} replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center text-chip-red">
        Erreur de connexion : {String((me.error as Error).message)}
      </div>
    );
  }

  return <>{children}</>;
}
